import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Deal from '@r6digital/rapidstor-types/types/Deal';

import { createSlice } from '@reduxjs/toolkit';

import { FormType } from '@/components/elements/contact-card';

import { RootState } from './index';

type ResponseType = string | boolean | number;

export interface UnitData {
    location: Location;
    unitType: UnitType;
    descriptors: Descriptor[];
    descriptor: Descriptor;
}

export interface ContactData {
    neededDate?: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
}

export interface ReservationData {
    unitType: UnitType;
    descriptor: Descriptor;
    coupon?: string | null;
    dealObject?: Deal;
    deal?: string;
    contactData?: ContactData;
    token?: string;
}

export interface ContactCardState {
    animate: string;
    type: FormType;
    unitType: UnitType | null;
    isDeal?: boolean;
    deal?: string | null;
}

interface State {
    unitData: UnitData | null;
    upSell: Descriptor | null;
    reservationData: ReservationData | null;
    contactData: ContactData | null;
    responses: Record<string, ResponseType | ResponseType[]>;
    locationCode: string;
    contactCard: ContactCardState;
    googleClickId: string | null;
    personaId: string | undefined;
}

const initialState: State = {
    unitData: null,
    upSell: null,
    reservationData: null,
    contactData: null,
    responses: {},
    locationCode: '',
    contactCard: {
        animate: 'initial',
        type: 'contactMe',
        unitType: null,
        isDeal: false,
        deal: null
    },
    googleClickId: null,
    personaId: undefined,
};

export const bookingSlice = createSlice({
    name: 'booking',
    initialState,
    reducers: {
        setUnitData: (state, action: { payload: UnitData; type: string }) => {
            state.unitData = action.payload;
        },
        setUpsell: (state, action: { payload: Descriptor | null; type: string }) => {
            state.upSell = action.payload;
        },
        setReservationData: (
            state,
            action: { payload: ReservationData; type: string }
        ) => {
            state.reservationData = action.payload;
        },
        setContactData: (state, action: { payload: ContactData; type: string }) => {
            state.contactData = action.payload;
        },
        setResponse: (state, action) => {
            const { id, response } = action.payload;
            state.responses[id] = response;
        },
        setLocationCode: (state, action) => {
            const { locationCode } = action.payload;
            state.locationCode = locationCode;
        },
        setContactCard: (state, action: { payload: ContactCardState; type: string }) => {
            state.contactCard = action.payload;
        },
        resetState: (state) => {
            Object.assign(state, initialState);
        },
        setGoogleClickId: (state, action: { payload: string | null; type: string }) => {
            state.googleClickId = action.payload;
        },
        setPersonaId: (state, action: { payload: string; type: string }) => {
            state.personaId = action.payload;
        },
    },
});

export const {
    setUnitData,
    setUpsell,
    setReservationData,
    setContactData,
    setResponse,
    setLocationCode,
    setContactCard,
    resetState,
    setGoogleClickId,
    setPersonaId,
} = bookingSlice.actions;

export const selectUnitData = (state: RootState) => state.booking.unitData;
export const selectUpsell = (state: RootState) => state.booking.upSell;
export const selectReservationData = (state: RootState) => state.booking.reservationData;
export const selectContactData = (state: RootState) => state.booking.contactData;
export const selectResponses = (state: RootState) => state.booking.responses;
export const selectLocationCode = (state: RootState) => state.booking.locationCode;
export const selectContactCard = (state: RootState) => state.booking.contactCard;
export const selectGoogleClickId = (state: RootState) => state.booking.googleClickId;
export const selectPersonaId = (state: RootState) => state.booking.personaId;
